/** @format */

/**
 * Shared time-edit cell — a scroller popover modelled on the "create meeting"
 * time picker (MeetingTimePicker): a 15-minute slot list that auto-scrolls to
 * the current value, with a free-form text field for typing. Promoted into
 * @clarix/ui-shared so the DataTable's `time` cell and the meeting form can
 * share ONE look. Colours come from design tokens (no hardcoded indigo).
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@clarix/ui/lib/cn';
import { Popover, PopoverTrigger, PopoverContent, Input } from '@clarix/ui';
import { useLockScrollWhileOpen } from './useScrollLock';

interface TimeSlot {
  label: string;
  value: string;
}

/** 96 slots at 15-minute increments: 24h `value` ("HH:mm") + 12h `label`. */
function generateTimeSlots(stepMinutes = 15): TimeSlot[] {
  const slots: TimeSlot[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      slots.push({ value, label: `${hour12}:${String(m).padStart(2, '0')} ${ampm}` });
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

/** "HH:mm" → "h:mm AM/PM" (empty-safe). */
function formatHHmmTo12h(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm.trim());
  if (!m) return '';
  const h = parseInt(m[1]!, 10);
  const min = m[2]!;
  if (Number.isNaN(h)) return '';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${min} ${h < 12 ? 'AM' : 'PM'}`;
}

/** Parse "14:07" or "2:07 PM" / "2:07pm" → "HH:mm" (or null when invalid). */
function parseTimeInput(input: string): string | null {
  const trimmed = input.trim();
  const m24 = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (m24) {
    const h = parseInt(m24[1]!, 10);
    const min = parseInt(m24[2]!, 10);
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59)
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  const m12 = /^(\d{1,2}):(\d{2})\s*(am|pm|a|p)$/i.exec(trimmed);
  if (m12) {
    let h = parseInt(m12[1]!, 10);
    const min = parseInt(m12[2]!, 10);
    const period = m12[3]!.toLowerCase();
    if (h < 1 || h > 12 || min < 0 || min > 59) return null;
    if (period.startsWith('p') && h !== 12) h += 12;
    if (period.startsWith('a') && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  return null;
}

interface TimePickerCellProps {
  /** Current value as "HH:mm" (or empty). */
  value: string;
  /** Read-mode content shown as the popover trigger (the cell's time Field). */
  display: ReactNode;
  /** Commit a new value ("HH:mm") or null when cleared. */
  onCommit: (next: string | null) => void | Promise<void>;
  /** Optional minimum selectable time ("HH:mm") — earlier slots are hidden. */
  minTime?: string;
  ariaLabel?: string;
}

export function TimePickerCell({ value, display, onCommit, minTime, ariaLabel }: TimePickerCellProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatHHmmTo12h(value));
  const [error, setError] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useLockScrollWhileOpen(open, contentRef);

  // Keep the editable text in sync when the value changes externally.
  const [prev, setPrev] = useState(value);
  if (value !== prev) {
    setPrev(value);
    setText(formatHHmmTo12h(value));
    setError(false);
  }

  const slots = useMemo(
    () => (minTime ? TIME_SLOTS.filter((s) => s.value >= minTime) : TIME_SLOTS),
    [minTime],
  );

  // Centre the active slot when the popover opens.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'center' });
    }, 30);
    return () => window.clearTimeout(id);
  }, [open]);

  const selectSlot = (slot: string) => {
    void onCommit(slot);
    setOpen(false);
    setError(false);
  };

  /** Commit the typed text. `close` only when invoked via Enter. */
  const commitText = (t: string, close = false) => {
    if (t.trim() === '') {
      void onCommit(null);
      if (close) setOpen(false);
      return;
    }
    const parsed = parseTimeInput(t);
    if (parsed) {
      void onCommit(parsed);
      if (close) setOpen(false);
    } else {
      setError(true);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel ?? 'Edit time'}
          title="Edit time"
          className="-mx-1 flex w-full min-w-0 items-center gap-0.5 rounded px-1 text-left transition-colors hover:bg-gray-50"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="min-w-0 flex-1">{display}</span>
          {/* Pencil affordance pinned right, revealed on row hover — same as the
              other inline-editable cells. */}
          <Pencil className="text-muted-foreground/60 -mr-1 hidden h-3 w-3 shrink-0 group-hover/row:block" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        align="start"
        sideOffset={6}
        className="w-40 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-1.5">
          <Input
            autoFocus
            value={text}
            placeholder="e.g. 2:07 PM"
            onChange={(e) => {
              setText(e.target.value);
              setError(false);
            }}
            onBlur={() => commitText(text)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                commitText(text, true);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setText(formatHHmmTo12h(value));
                setError(false);
                setOpen(false);
              }
            }}
            // Same minimal look as the inline-edit cell input: a thin underline
            // (no box / ring) that darkens on focus.
            className={cn(
              'h-auto w-full rounded-none border-0 border-b border-border bg-transparent p-0 pb-1 text-md shadow-none transition-colors placeholder:text-sm focus-visible:border-foreground/40 focus-visible:ring-0 focus-visible:ring-offset-0',
              error && 'border-destructive text-destructive-fg focus-visible:border-destructive',
            )}
          />
        </div>
        <div
          ref={listRef}
          className="max-h-[200px] overflow-y-auto overflow-x-hidden p-1"
          onWheel={(e) => e.stopPropagation()}
        >
          {slots.map((slot) => {
            const isActive = slot.value === value;
            return (
              <button
                key={slot.value}
                type="button"
                data-active={isActive}
                onClick={() => selectSlot(slot.value)}
                className={cn(
                  'block w-full rounded-sm px-2 py-1 text-left text-md transition-colors',
                  isActive ? 'bg-info-light text-info-fg font-medium' : 'text-foreground hover:bg-muted',
                )}
              >
                {slot.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
